import React, { createContext, useEffect } from 'react'

import useAuth from 'hooks/useAuth'
import Spinner from 'components/Spinner'
import AuthInterstitial from 'components/Factset/AuthInterstitial'
import { useUnleashContext } from '@unleash/proxy-client-react'

const SessionStateContext = createContext()
const SessionHandlerContext = createContext()

function SessionProvider({ children }) {
  const { state, handlers } = useAuth()
  const updateContext = useUnleashContext()

  useEffect(() => {
    if (!state.currentUser?.id) {
      return
    }

    async function applyUserContext() {
      try {
        await updateContext({ userId: state.currentUser.id })
      } catch {
        // ignore — fall back to cached/default flags; they refresh on the next poll or page load
      }
    }
    applyUserContext()
  }, [updateContext, state.currentUser])

  if (state.isSignedOut) {
    return <AuthInterstitial message="You successfully logged out" onSignIn={handlers.signIn} />
  }

  if (state.isSessionExpired) {
    return (
      <AuthInterstitial
        message="You've been logged out due to inactivity"
        onSignIn={handlers.signIn}
      />
    )
  }

  if (state.needsFactsetLogin || state.oidcCallbackFailed) {
    return <AuthInterstitial message="Log in to your account" onSignIn={handlers.signIn} />
  }

  if (state.isLoading || state.currentUser === null) {
    return <Spinner size={75} fullPage />
  }

  return (
    <SessionStateContext.Provider value={state}>
      <SessionHandlerContext.Provider value={handlers}>
        {state.currentUser && children}
      </SessionHandlerContext.Provider>
    </SessionStateContext.Provider>
  )
}

function useSessionState() {
  const context = React.useContext(SessionStateContext)
  if (context === undefined) {
    throw new Error('useSessionState must be used within an SessionProvider')
  }
  return context
}

function useSessionHandlers() {
  const context = React.useContext(SessionHandlerContext)
  if (context === undefined) {
    throw new Error('useSessionHandlers must be used within an SessionProvider')
  }
  return context
}

export { useSessionState, useSessionHandlers, SessionProvider, SessionHandlerContext }
